import { ScaleuiWrapper } from "@scaleaq/sui-react/wrappers/ScaleuiWrapper";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import i18next from "src/datahandling/localization/i18n";
import {
	DEFAULT_LIVE_PROFILE,
	DEFAULT_USER_SETTINGS,
} from "src/models/constants";
import type { LanguageType, ThemeMode } from "src/models/types/common.types";
import type {
	ApplicationFeaturesType,
	CompanyType,
	InsightsLiveUserSettingsType,
	LiveConfigurationsType,
	LiveProfileType,
	UserSettings,
} from "src/models/types/userSettings.types";
import { useUserSettingsConfig } from "../hooks";

type ProfileWithId = LiveProfileType & { profileId?: string };

type CustomerSettingsContextProps = {
	features: ApplicationFeaturesType;
	setFeatures: (newFeatures: ApplicationFeaturesType) => void;
	language: string;
	setLanguage: (newLanguage: string) => void;
	theme: ThemeMode;
	setTheme: (newTheme: string) => void;
	selectedCompany: CompanyType | undefined;
	setSelectedCompany: (newCompany: CompanyType) => void;
	decimalCount: number;
	setDecimalCount: (newDecimalCount: number) => void;
	customerSettings: InsightsLiveUserSettingsType;
	setCustomerSettings: (newUserSettings: InsightsLiveUserSettingsType) => void;
	getUserSettingsLiveView: (viewId: string) => LiveProfileType;
	setUserSettingsLiveView: (view: LiveProfileType) => void;
	setUserSettingsLiveProfile: (newProfile: LiveProfileType) => void;
	getUserSettingsLiveProfile: (profileId: string) => LiveProfileType;
	isLoading: boolean;
};

export const CustomerSettingsContext = createContext<CustomerSettingsContextProps>({
	features: {},
	setFeatures: () => {},
	language: "en",
	setLanguage: () => {},
	theme: "light",
	setTheme: () => {},
	decimalCount: 3,
	setDecimalCount: () => {},
	selectedCompany: undefined,
	setSelectedCompany: () => {},
	customerSettings: DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive,
	setCustomerSettings: () => {},
	setUserSettingsLiveProfile: () => {},
	getUserSettingsLiveProfile: () => DEFAULT_LIVE_PROFILE,
	getUserSettingsLiveView: () => DEFAULT_LIVE_PROFILE,
	setUserSettingsLiveView: () => {},
	isLoading: true,
});

export const CustomerSettingsWrapper = (props: { children: React.ReactNode }) => {
	// *****************************************************
	// Properties
	// *****************************************************

	const { userSettings, setUserSettings, isLoading } = useUserSettingsConfig();

	// *****************************************************
	// Lifecycle
	// *****************************************************

	// Initialize from userSettings or defaults
	const initialCustomerSettings = useMemo(
		() =>
			userSettings?.applicationSpecific?.insightsLive ||
			DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive,
		[userSettings?.applicationSpecific?.insightsLive],
	);

	const [theme, _setTheme] = useState<ThemeMode>(() => {
		const defaultTheme = DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive.theme;
		return (initialCustomerSettings.theme ?? defaultTheme) as ThemeMode;
	});
	const [features, _setFeatures] = useState<ApplicationFeaturesType>(() => {
		return initialCustomerSettings.features ?? {};
	});
	const [language, _setLanguage] = useState<string>(() => {
		const defaultLanguage = DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive.language;
		return (initialCustomerSettings.language ?? defaultLanguage) as string;
	});
	const [selectedCompany, _setSelectedCompany] = useState<CompanyType>(() => {
		const defaultSelectedCompany = DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive.selectedCompany;
		return (initialCustomerSettings.selectedCompany ?? defaultSelectedCompany) as CompanyType;
	});
	const [decimalCount, _setDecimalCount] = useState<number>(() => {
		const defaultDecimalCount = DEFAULT_USER_SETTINGS.applicationSpecific.insightsLive.decimalCount;
		return (initialCustomerSettings.decimalCount ?? defaultDecimalCount) as number;
	});

	const [customerSettings, _setCustomerSettings] =
		useState<InsightsLiveUserSettingsType>(initialCustomerSettings);

	// Refs to avoid stale closures in callbacks
	const customerSettingsRef = useRef(customerSettings);
	const userSettingsRef = useRef(userSettings);

	// Keep refs in sync
	useEffect(() => {
		customerSettingsRef.current = customerSettings;
	}, [customerSettings]);

	useEffect(() => {
		userSettingsRef.current = userSettings;
	}, [userSettings]);

	// Sync customerSettings, theme, and language when userSettings changes
	useEffect(() => {
		if (userSettings?.applicationSpecific?.insightsLive) {
			const newCustomerSettings = userSettings.applicationSpecific.insightsLive;

			_setCustomerSettings(newCustomerSettings);

			if (newCustomerSettings.theme) {
				_setTheme(newCustomerSettings.theme);
			}
			if (newCustomerSettings.language) {
				_setLanguage(newCustomerSettings.language);
			}
			if (newCustomerSettings.features) {
				_setFeatures(newCustomerSettings.features);
			}
			// Use !== undefined to allow 0 as a valid value
			if (newCustomerSettings.decimalCount !== undefined) {
				_setDecimalCount(newCustomerSettings.decimalCount);
			}
			if(newCustomerSettings.selectedCompany) {
				_setSelectedCompany(newCustomerSettings.selectedCompany);
			}
		}
	}, [userSettings?.applicationSpecific?.insightsLive]);

	// Sync i18next language when language state changes
	useEffect(() => {
		if (language && i18next.language !== language) {
			i18next.changeLanguage(language);
		}
	}, [language]);

	// *****************************************************
	// Handling
	// *****************************************************

	const setFeatures = useCallback((newFeatures: ApplicationFeaturesType) => {
		_setFeatures(newFeatures);
	}, []);

	const setLanguage = useCallback(
		async (newLanguage: string) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings) return;

			_setLanguage(newLanguage);

			// Update customerSettings with new language
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				language: newLanguage as LanguageType,
			};
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
				// Also update common settings for consistency
				common: {
					...(currentUserSettings.common || {}),
					language: newLanguage,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const setTheme = useCallback(
		async (newTheme: ThemeMode) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings) return;

			_setTheme(newTheme);

			// Update customerSettings with new theme
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				theme: newTheme,
			};
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
				// Also update common settings for consistency
				common: {
					...(currentUserSettings.common || {}),
					uiTheme: newTheme,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const setSelectedCompany = useCallback(
		async (newCompany: CompanyType) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings) return;

			_setSelectedCompany(newCompany);

			// Update customerSettings with new selectedCompany
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				selectedCompany: newCompany,
			};
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const setCustomerSettings = useCallback((newConfigurations: InsightsLiveUserSettingsType) => {
		_setCustomerSettings(newConfigurations);
	}, []);

	const setDecimalCount = useCallback(
		async (newDecimalCount: number) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings) return;

			_setDecimalCount(newDecimalCount);

			// Update customerSettings with new decimalCount
			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				decimalCount: newDecimalCount,
			};
			_setCustomerSettings(updatedCustomerSettings);

			// Update userSettings in backend
			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	// *****************************************************
	// Handling :: User Settings
	// *****************************************************

	const getUserSettingsLiveView = useCallback(
		(viewId: string): LiveProfileType => {
			const views = customerSettings?.liveConfigurations?.views || [];
			const view = views.find((v) => v.id === viewId || (v as ProfileWithId).profileId === viewId);

			// Only fall back to DEFAULT when there are no views at all from the API
			if (!view && views.length === 0) {
				return DEFAULT_LIVE_PROFILE;
			}

			return view || DEFAULT_LIVE_PROFILE;
		},
		[customerSettings],
	);

	const setUserSettingsLiveView = useCallback(
		async (newView: LiveProfileType) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings || !currentCustomerSettings) return;

			const viewId = newView.id || (newView as ProfileWithId).profileId;
			if (!viewId) {
				console.error("View must have an id or profileId");
				return;
			}

			const existingViews = currentCustomerSettings.liveConfigurations?.views || [];
			const existingProfiles = currentCustomerSettings.liveConfigurations?.profiles || [];

			const existingViewIndex = existingViews.findIndex(
				(view) => view.id === viewId || (view as ProfileWithId).profileId === viewId,
			);

			// Replace the view entirely with the new data -- do not merge with old
			const cleanView: LiveProfileType = { ...newView, id: viewId };

			const updatedViews: LiveProfileType[] =
				existingViewIndex >= 0
					? existingViews.map((view, index) =>
							index === existingViewIndex ? cleanView : view,
						)
					: [...existingViews, cleanView];

			// Build a clean liveConfigurations object
			const updatedLiveConfigurations: LiveConfigurationsType = {
				profiles: existingProfiles,
				views: updatedViews,
			};

			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				liveConfigurations: updatedLiveConfigurations,
			};

			_setCustomerSettings(updatedCustomerSettings);

			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const setUserSettingsLiveProfile = useCallback(
		async (newProfile: LiveProfileType) => {
			const currentUserSettings = userSettingsRef.current;
			const currentCustomerSettings = customerSettingsRef.current;

			if (!currentUserSettings || !currentCustomerSettings) return;

			const profileId = newProfile.id || (newProfile as ProfileWithId).profileId;
			if (!profileId) {
				console.error("Profile must have an id or profileId");
				return;
			}

			const existingProfiles = currentCustomerSettings.liveConfigurations?.profiles || [];
			const existingViews = currentCustomerSettings.liveConfigurations?.views || [];

			// Build the clean profile from the UI data only -- do not merge with old
			const cleanProfile: LiveProfileType = { ...newProfile, id: profileId };

			const existingProfileIndex = existingProfiles.findIndex(
				(profile) => profile.id === profileId || (profile as ProfileWithId).profileId === profileId,
			);

			const updatedProfiles: LiveProfileType[] =
				existingProfileIndex >= 0
					? existingProfiles.map((profile, index) =>
							index === existingProfileIndex ? cleanProfile : profile,
						)
					: [...existingProfiles, cleanProfile];

			// Sync the corresponding view with the same clean profile data
			const existingViewIndex = existingViews.findIndex(
				(view) => view.id === profileId || (view as ProfileWithId).profileId === profileId,
			);

			const updatedViews: LiveProfileType[] =
				existingViewIndex >= 0
					? existingViews.map((view, index) =>
							index === existingViewIndex ? cleanProfile : view,
						)
					: [...existingViews, cleanProfile];

			// Build a clean liveConfigurations object -- no spreading of old config
			const updatedLiveConfigurations: LiveConfigurationsType = {
				profiles: updatedProfiles,
				views: updatedViews,
			};

			const updatedCustomerSettings: InsightsLiveUserSettingsType = {
				...currentCustomerSettings,
				liveConfigurations: updatedLiveConfigurations,
			};

			_setCustomerSettings(updatedCustomerSettings);

			const updatedUserSettings: UserSettings = {
				...currentUserSettings,
				applicationSpecific: {
					...(currentUserSettings.applicationSpecific || {}),
					insightsLive: updatedCustomerSettings,
				},
			};

			await setUserSettings(updatedUserSettings);
		},
		[setUserSettings],
	);

	const getUserSettingsLiveProfile = useCallback(
		(profileId: string): LiveProfileType => {
			const profiles = customerSettings?.liveConfigurations?.profiles || [];
			const profile = profiles.find(
				(p) => p.id === profileId || (p as ProfileWithId).profileId === profileId,
			);

			// Only fall back to DEFAULT when there are no profiles at all from the API
			if (!profile && profiles.length === 0) {
				return DEFAULT_LIVE_PROFILE;
			}

			return profile || DEFAULT_LIVE_PROFILE;
		},
		[customerSettings],
	);

	// *****************************************************
	// Context
	// *****************************************************

	const context = useMemo(() => {
		return {
			features,
			setFeatures,
			language,
			setLanguage,
			theme,
			setTheme,
			selectedCompany,
			setSelectedCompany,
			customerSettings,
			setCustomerSettings,
			decimalCount,
			setDecimalCount,
			setUserSettingsLiveProfile,
			getUserSettingsLiveProfile,
			getUserSettingsLiveView,
			setUserSettingsLiveView,
			isLoading,
		};
	}, [
		features,
		language,
		theme,
		selectedCompany,
		customerSettings,
		decimalCount,
		setCustomerSettings,
		setTheme,
		setLanguage,
		setSelectedCompany,
		setFeatures,
		setUserSettingsLiveProfile,
		getUserSettingsLiveProfile,
		getUserSettingsLiveView,
		setUserSettingsLiveView,
		setDecimalCount,
		isLoading,
	]);

	// *****************************************************
	// Render
	// *****************************************************

	// Don't render children until settings are loaded
	if (isLoading) {
		return null;
	}

	return (
		<CustomerSettingsContext.Provider value={context as CustomerSettingsContextProps}>
			<ScaleuiWrapper theme={theme} locale={language}>
				{props.children}
			</ScaleuiWrapper>
		</CustomerSettingsContext.Provider>
	);
};
